import { supabaseClient } from "@/lib/supabase";
import { DEFAULT_LOCALE, readStoredLocale, resolveLocale, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import type { ConsentSignatureT, ConsentT, ConsentViewT } from "@/types/database/consents";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { QueryOptions, UseSuspenseQueryOptions } from "@tanstack/react-query";
import { localizeUnsignedConsent, localizeUnsignedConsents } from "./fetchConsentTranslations";

export function resolveConsentReadLocale(locale?: SupportedLocale): SupportedLocale {
    return locale ?? resolveLocale({ storedLocale: readStoredLocale() }) ?? DEFAULT_LOCALE;
}

export const consentKeys = {
    all: ["consents"] as const,
    byIdPrefix: (id: string) => [...consentKeys.all, "byId", id] as const,
    byId: (id: string, locale: SupportedLocale = DEFAULT_LOCALE) => [...consentKeys.byIdPrefix(id), locale] as const,
    byDiaryIdPrefix: (diaryId: string) => [...consentKeys.all, "byDiaryId", diaryId] as const,
    byDiaryId: (diaryId: string, locale: SupportedLocale = DEFAULT_LOCALE) =>
        [...consentKeys.byDiaryIdPrefix(diaryId), locale] as const,
};

/** Keep consent-by-id cache in sync immediately after signing so the complete route can render. */
export function patchConsentSignedInCache(
    queryClient: QueryClient,
    consentId: string,
    userId: string,
    signedAt: string,
    signedConsentVersion?: string | null,
    locale: SupportedLocale = DEFAULT_LOCALE,
    signedLocale?: string | null
): void {
    queryClient.setQueryData<ConsentViewT | null>(consentKeys.byId(consentId, locale), (existing) => {
        if (!existing) return existing;

        const signatures = Array.isArray(existing.signatures) ? [...existing.signatures] : [];
        const signatureIndex = signatures.findIndex((signature) => signature.user_id === userId);

        if (signatureIndex >= 0) {
            signatures[signatureIndex] = {
                ...signatures[signatureIndex],
                signed_at: signedAt,
                signed_consent_version:
                    signedConsentVersion ?? signatures[signatureIndex]?.signed_consent_version ?? null,
                signed_locale: signedLocale ?? signatures[signatureIndex]?.signed_locale ?? null,
            } as ConsentSignatureT;
        } else {
            signatures.push({
                consent_id: consentId,
                signature_id: crypto.randomUUID(),
                user_id: userId,
                signed_at: signedAt,
                signed_consent_version: signedConsentVersion ?? null,
                signed_locale: signedLocale ?? null,
                created_at: signedAt,
                updated_at: signedAt,
                ip_address: null,
            });
        }

        return {
            ...existing,
            signatures,
            has_signatures: true,
        };
    });
}

export async function fetchConsentById(
    consentId: string,
    locale: SupportedLocale = resolveConsentReadLocale()
): Promise<ConsentViewT | null> {
    const { data, error } = await supabaseClient
        .from("consents_view")
        .select("*")
        .eq("consent_id", consentId)
        .maybeSingle();

    if (error) {
        throw error;
    }

    if (!data) {
        return null;
    }

    return localizeUnsignedConsent(data as ConsentViewT, locale);
}

type UseConsentByIdOptions = {
    enabled?: boolean;
};

export function useConsentById(consentId: string, options?: UseConsentByIdOptions) {
    const { locale } = useAppLocale();
    const resolvedLocale = resolveConsentReadLocale(locale);
    return useQuery({
        queryKey: consentKeys.byId(consentId, resolvedLocale),
        queryFn: () => fetchConsentById(consentId, resolvedLocale),
        enabled: !!consentId && options?.enabled !== false,
    });
}

export function useSuspenseConsentById(
    consentId: string,
    options?: Omit<UseSuspenseQueryOptions<ConsentViewT | null>, "queryKey" | "queryFn">
) {
    const { locale } = useAppLocale();
    const resolvedLocale = resolveConsentReadLocale(locale);
    return useSuspenseQuery<ConsentViewT | null>({
        queryKey: consentKeys.byId(consentId, resolvedLocale),
        queryFn: () => fetchConsentById(consentId, resolvedLocale),
        ...options,
    });
}

export function getConsentByIdOptions(
    consentId: string,
    locale?: SupportedLocale
): Omit<QueryOptions<ConsentViewT | null>, "queryKey"> & {
    queryKey: readonly unknown[];
} {
    const resolvedLocale = resolveConsentReadLocale(locale);
    return {
        queryKey: consentKeys.byId(consentId, resolvedLocale),
        queryFn: () => fetchConsentById(consentId, resolvedLocale),
    };
}

/**
 * Fetches all consents for a diary
 */
export async function fetchConsentsByDiaryId(diaryId: string): Promise<ConsentT[]> {
    const { data, error } = await supabaseClient.from("consents").select("*").eq("diary_id", diaryId);

    if (error) {
        throw error;
    }

    return data as ConsentT[];
}

/**
 * Fetches all consents with signatures for a diary
 */
export async function fetchConsentViewsByDiaryId(
    diaryId: string,
    locale: SupportedLocale = resolveConsentReadLocale()
): Promise<ConsentViewT[]> {
    const { data, error } = await supabaseClient.from("consents_view").select("*").eq("diary_id", diaryId);

    if (error) {
        throw error;
    }

    return localizeUnsignedConsents((data ?? []) as ConsentViewT[], locale);
}

export function useConsentsByDiaryId(diaryId: string, options?: UseConsentByIdOptions) {
    const { locale } = useAppLocale();
    const resolvedLocale = resolveConsentReadLocale(locale);
    return useQuery({
        queryKey: consentKeys.byDiaryId(diaryId, resolvedLocale),
        queryFn: () => fetchConsentViewsByDiaryId(diaryId, resolvedLocale),
        enabled: !!diaryId && options?.enabled !== false,
    });
}

export function useSuspenseConsentsByDiaryId(
    diaryId: string,
    options?: Omit<UseSuspenseQueryOptions<ConsentViewT[]>, "queryKey" | "queryFn">
) {
    const { locale } = useAppLocale();
    const resolvedLocale = resolveConsentReadLocale(locale);
    return useSuspenseQuery<ConsentViewT[]>({
        queryKey: consentKeys.byDiaryId(diaryId, resolvedLocale),
        queryFn: () => fetchConsentViewsByDiaryId(diaryId, resolvedLocale),
        ...options,
    });
}
